import React from "react";
import { Box } from "@mui/joy";
import Rating from "@mui/material/Rating";
import { GroupedActivityItem, ActivityType } from "../../store/activityStore";

interface PostcardActivityCardProps {
  activity: GroupedActivityItem;
}

const getStampEmoji = (type: ActivityType): string => {
  switch (type) {
    case "list_created":
      return "🗺️";
    case "place_added":
      return "📍";
    case "list_saved":
      return "🔖";
    default:
      return "📝";
  }
};

const getActivityLabel = (type: ActivityType): string => {
  switch (type) {
    case "list_created":
      return "New List";
    case "place_added":
      return "Place Added";
    case "list_saved":
      return "List Saved";
    default:
      return "Activity";
  }
};

const getRotation = (type: ActivityType): string => {
  switch (type) {
    case "list_created":
      return "rotate(-0.4deg)";
    case "place_added":
      return "rotate(0.3deg)";
    case "list_saved":
      return "rotate(0.2deg)";
    default:
      return "rotate(0deg)";
  }
};

const getMainText = (activity: GroupedActivityItem): string => {
  switch (activity.activity_type) {
    case "place_added":
      return activity.place?.name || "a place";
    case "list_created":
    case "list_saved":
      return activity.list?.title || "a list";
    default:
      return activity.list?.title || "a list";
  }
};

const getSubtitle = (activity: GroupedActivityItem): string => {
  const listTitle = activity.list?.title || "a list";
  switch (activity.activity_type) {
    case "place_added":
      return `added to "${listTitle}"`;
    case "list_created":
      return "a new trip is being planned!";
    case "list_saved":
      return "saved to collection";
    default:
      return "";
  }
};

export const PostcardActivityCard: React.FC<PostcardActivityCardProps> = ({ activity }) => {
  return (
    <Box
      data-testid="postcard-activity-card"
      sx={{
        backgroundColor: "#fffdf9",
        borderRadius: "4px",
        boxShadow: "0 3px 14px rgba(0,0,0,0.08)",
        padding: "20px",
        transform: getRotation(activity.activity_type),
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Stamp */}
      <Box
        sx={{
          position: "absolute",
          top: 12,
          right: 14,
          width: 40,
          height: 46,
          border: "1.5px dashed #ccc",
          borderRadius: "2px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255,245,230,0.5)",
        }}
      >
        <span style={{ fontSize: "18px" }} data-testid="stamp-emoji">
          {getStampEmoji(activity.activity_type)}
        </span>
      </Box>

      {/* Content */}
      <Box sx={{ maxWidth: "75%" }}>
        <Box
          data-testid="activity-label"
          sx={{
            fontSize: "10px",
            color: "#bbb",
            textTransform: "uppercase",
            letterSpacing: "1.5px",
            marginBottom: "6px",
          }}
        >
          {getActivityLabel(activity.activity_type)}
        </Box>
        <Box
          sx={{
            fontFamily: "'Georgia', serif",
            fontSize: "17px",
            color: "#2a2a2a",
            fontWeight: 600,
            lineHeight: 1.3,
          }}
        >
          {getMainText(activity)}
        </Box>
        <Box
          sx={{
            fontSize: "12px",
            color: "#999",
            marginTop: "4px",
          }}
        >
          {getSubtitle(activity)}
        </Box>
        {activity.place?.rating != null && activity.place.rating > 0 && (
          <Rating
            value={activity.place.rating}
            precision={0.5}
            readOnly
            size="small"
            sx={{ marginTop: "6px", opacity: 0.8 }}
          />
        )}
        {activity.place?.notes && (
          <Box
            sx={{
              marginTop: "8px",
              fontFamily: "'Georgia', serif",
              fontStyle: "italic",
              fontSize: "12px",
              color: "#888",
              lineHeight: 1.5,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {activity.place.notes}
          </Box>
        )}
      </Box>

      {/* Decorative ruled lines */}
      <Box sx={{ marginTop: "16px", borderTop: "1px solid #e8e0d4" }} />
      <Box sx={{ marginTop: "8px", borderTop: "1px solid #e8e0d4" }} />
    </Box>
  );
};
